import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import capi from "@/lib/customerApi";
import { apiErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { MailCheck } from "lucide-react";
import { toast } from "sonner";
import { AuthShell, MockOtpNote } from "@/pages/customer/AuthShell";
import { Field } from "@/pages/customer/SignUp";

export default function ForgotPassword() {
  return (
    <AuthShell eyebrow="Account recovery" title="Forgot password" subtitle="Get a reset link by email, or an OTP on your mobile." testid="forgot-page">
      <Tabs defaultValue="email">
        <TabsList className="grid grid-cols-2 w-full rounded-none">
          <TabsTrigger value="email" className="rounded-none" data-testid="fp-tab-email">Email link</TabsTrigger>
          <TabsTrigger value="mobile" className="rounded-none" data-testid="fp-tab-mobile">Mobile OTP</TabsTrigger>
        </TabsList>
        <TabsContent value="email" className="mt-6"><EmailReset /></TabsContent>
        <TabsContent value="mobile" className="mt-6"><MobileReset /></TabsContent>
      </Tabs>
      <Link to="/signin" className="block text-center text-sm text-muted-foreground hover:text-primary mt-6" data-testid="fp-back-signin">← Back to sign in</Link>
    </AuthShell>
  );
}

function EmailReset() {
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await capi.post("/auth/forgot-password", { email });
      setSent(true);
    } catch (err) {
      toast.error(apiErr(err));
    } finally {
      setBusy(false);
    }
  };
  if (sent) {
    return (
      <div className="text-center space-y-2" data-testid="fp-email-sent">
        <MailCheck className="h-10 w-10 text-primary mx-auto" />
        <p className="text-sm">If that email is registered, a reset link has been sent. It expires in 1 hour.</p>
      </div>
    );
  }
  return (
    <form onSubmit={submit} className="space-y-4">
      <Field id="fp-email" label="Email Address" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required />
      <Button type="submit" disabled={busy} className="w-full rounded-none h-11 font-bold uppercase tracking-wide" data-testid="fp-email-submit">{busy ? "Sending…" : "Send reset link"}</Button>
    </form>
  );
}

function MobileReset() {
  const nav = useNavigate();
  const [mobile, setMobile] = useState("");
  const [sent, setSent] = useState(false);
  const [mockCode, setMockCode] = useState("");
  const [f, setF] = useState({ otp: "", password: "", confirm_password: "" });
  const [busy, setBusy] = useState(false);

  const sendOtp = async (e) => {
    e?.preventDefault();
    if (!/^[6-9]\d{9}$/.test(mobile)) return toast.error("Enter a valid 10-digit mobile number");
    setBusy(true);
    try {
      const { data } = await capi.post("/auth/otp/send", { mobile, purpose: "reset" });
      setSent(true);
      setMockCode(data.mock_otp || "");
    } catch (err) {
      toast.error(apiErr(err));
    } finally {
      setBusy(false);
    }
  };

  const reset = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await capi.post("/auth/reset-password-otp", { mobile, ...f });
      toast.success("Password updated. Please sign in.");
      nav("/signin", { replace: true });
    } catch (err) {
      toast.error(apiErr(err));
    } finally {
      setBusy(false);
    }
  };

  if (!sent) {
    return (
      <form onSubmit={sendOtp} className="space-y-4">
        <Field id="fp-mobile" label="Registered Mobile Number" inputMode="numeric" maxLength={10} value={mobile} onChange={(e) => setMobile(e.target.value.replace(/\D/g, ""))} required />
        <Button type="submit" disabled={busy} className="w-full rounded-none h-11 font-bold uppercase tracking-wide" data-testid="fp-send-otp">{busy ? "Sending…" : "Send OTP"}</Button>
      </form>
    );
  }
  return (
    <form onSubmit={reset} className="space-y-4" data-testid="fp-otp-form">
      <p className="text-xs text-muted-foreground">If +91 {mobile} is registered, an OTP has been sent.</p>
      <MockOtpNote code={mockCode} />
      <Field id="fp-otp" label="OTP" inputMode="numeric" maxLength={6} value={f.otp} onChange={(e) => setF((s) => ({ ...s, otp: e.target.value.replace(/\D/g, "") }))} required />
      <Field id="fp-new-password" label="New Password" type="password" value={f.password} onChange={(e) => setF((s) => ({ ...s, password: e.target.value }))} required hint="At least 8 characters" />
      <Field id="fp-confirm-password" label="Confirm New Password" type="password" value={f.confirm_password} onChange={(e) => setF((s) => ({ ...s, confirm_password: e.target.value }))} required />
      <Button type="submit" disabled={busy} className="w-full rounded-none h-11 font-bold uppercase tracking-wide" data-testid="fp-otp-submit">{busy ? "Updating…" : "Reset password"}</Button>
      <button type="button" onClick={() => sendOtp()} className="text-xs text-primary hover:underline" data-testid="fp-resend-otp">Resend OTP</button>
    </form>
  );
}
